import React, { useEffect, useState } from 'react';
import {
  LayoutDashboard, Users, Server, HeadphonesIcon,
  Search, ChevronLeft, UserPlus, Ban, CheckCircle, Trash2,
  AlertTriangle, Clock, RefreshCw, Eye, Settings, FileText, KeyRound,
  Shield, Save, ArrowLeft, Send, Wrench, Download,
  BarChart3, Megaphone, Plus, X,
} from 'lucide-react';
import { adminApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { SkeletonCard, SkeletonTable } from '../components/Skeleton';
import AdminWartung from './AdminWartung';
import './Pages.css';

// --- Admin Dashboard ---
function AdminDashboard() {
  const [stats, setStats] = useState<any>(null);
  useEffect(() => { adminApi.dashboard().then(r => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
      {[...Array(5)].map((_, i) => <SkeletonCard key={i} />)}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Registrierte Nutzer', value: stats.total_users, color: '#8B6F47' },
          { label: 'Aktive Abos', value: stats.active_subscriptions, color: '#2e7d32' },
          { label: 'Testphasen', value: stats.trial_subscriptions, color: '#B8860B' },
          { label: 'Neu (30 Tage)', value: stats.new_registrations_30d, color: '#4285F4' },
          { label: 'Offene Tickets', value: stats.open_support_tickets, color: stats.open_support_tickets > 0 ? '#991b1b' : '#888' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: 20, textAlign: 'center' }}>
            <p className="text-muted text-sm" style={{ margin: 0 }}>{label}</p>
            <p style={{ fontSize: '2rem', fontWeight: 700, margin: '4px 0 0', color }}>{value}</p>
          </div>
        ))}
      </div>
      {stats.plan_distribution && Object.keys(stats.plan_distribution).length > 0 && (() => {
        const planNames: Record<string, string> = { free: 'Free', solo: 'Free', basic: 'Einzelcoach', team_s: 'Professional S', team_m: 'Professional M', enterprise: 'Organisation' };
        const planColors: Record<string, string> = { free: '#B8860B', solo: '#B8860B', basic: '#2e7d32', team_s: '#4285F4', team_m: '#7B5EA7', enterprise: '#D4A017' };
        const entries = Object.entries(stats.plan_distribution);
        const maxCount = Math.max(...entries.map(([, c]) => c as number), 1);
        return (
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 16 }}>Plan-Verteilung</h3>
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', height: 140 }}>
              {entries.map(([plan, count]) => (
                <div key={plan} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: '100%', maxWidth: 120, borderRadius: '6px 6px 0 0',
                    height: Math.max(20, ((count as number) / maxCount) * 110),
                    background: planColors[plan] || '#888',
                    transition: 'height 0.3s ease',
                  }} />
                  <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                    {planNames[plan] || plan} ({count as number})
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// --- Helpers ---
const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Superadmin', admin: 'Admin', coach: 'Coach',
  consultant: 'Berater', change_manager: 'Change-Manager', pair_counselor: 'Paarberatung',
};
const PLAN_LABELS: Record<string, string> = { free: 'Free Trial', solo: 'Free Trial', basic: 'Einzelcoach', team_s: 'Professional S', team_m: 'Professional M', enterprise: 'Organisation' };
const PLAN_BADGE: Record<string, { bg: string; color: string }> = {
  free: { bg: '#fef2f2', color: '#991b1b' }, solo: { bg: '#fef2f2', color: '#991b1b' },
  basic: { bg: '#fff8e1', color: '#B8860B' }, team_s: { bg: '#e3f2fd', color: '#1565c0' },
  team_m: { bg: '#e8f5e9', color: '#2e7d32' }, enterprise: { bg: '#fff3e0', color: '#e65100' },
};
const BETA_BADGE = { bg: '#ede9fe', color: '#6d28d9' };

// --- Admin Users ---
function AdminNutzer() {
  const { showToast } = useToast();
  const [users, setUsers] = useState<any[]>([]);
  const [subs, setSubs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'coach' });
  const [resetResult, setResetResult] = useState<{ tempPw?: string; email?: string } | null>(null);

  const loadUsers = async (q?: string) => {
    const res = await adminApi.users({ search: q || undefined, limit: 100 });
    setUsers(res.data.users);
    setTotal(res.data.total);
  };

  const loadSubs = async () => {
    try { const res = await adminApi.subscriptions(); setSubs(res.data.subscriptions); } catch {}
  };

  useEffect(() => { loadUsers(); loadSubs(); }, []);

  const getSubForUser = (userId: string) => subs.find((s) => s.user_id === userId);

  const loadDetail = async (id: string) => {
    const res = await adminApi.userDetail(id);
    setSelectedUser(res.data);
    setResetResult(null);
  };

  const handleCreate = async () => {
    if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) return;
    try {
      await adminApi.createUser(newUser);
      setShowCreate(false);
      setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'coach' });
      loadUsers(); loadSubs();
      showToast('Nutzer erfolgreich angelegt', 'success');
    } catch (err: any) { showToast(err.response?.data?.detail || 'Fehler', 'error'); }
  };

  const handleResetPassword = async (userId: string, userName: string, userEmail: string) => {
    if (!window.confirm(
      `Das aktuelle Passwort von ${userName} wird sofort ungültig.\n\nEin neues temporäres Passwort wird generiert und per E-Mail an ${userEmail} gesendet.\n\nFortfahren?`
    )) return;
    try {
      const res = await adminApi.resetUserPassword(userId);
      if (res.data.email_sent) {
        showToast(`Temporäres Passwort wurde per E-Mail an ${userEmail} gesendet`, 'success');
        setResetResult(null);
      } else if (res.data.temp_password) {
        setResetResult({ tempPw: res.data.temp_password, email: userEmail });
      } else {
        showToast('Passwort zurückgesetzt', 'success');
      }
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Zurücksetzen', 'error');
    }
  };

  // --- Detail View ---
  if (selectedUser) {
    const sub = getSubForUser(selectedUser.id);
    const planKey = sub?.plan_type || selectedUser.plan || '';
    const badge = PLAN_BADGE[planKey] || { bg: 'var(--bg)', color: 'var(--text-muted)' };
    const statusColor = (s: string) => s === 'active' ? '#2e7d32' : s === 'trial' ? '#B8860B' : s === 'past_due' ? '#991b1b' : '#888';

    return (
      <div>
        <button className="btn btn-sm btn-secondary" onClick={() => { setSelectedUser(null); setResetResult(null); }} style={{ marginBottom: 16 }}>
          <ChevronLeft size={14} /> Zurück
        </button>
        <div className="card" style={{ padding: 24 }}>
          <h3>{selectedUser.first_name} {selectedUser.last_name}</h3>
          <p className="text-muted text-sm">{selectedUser.email}</p>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 12, marginTop: 16 }}>
            <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8 }}>
              <p className="text-muted text-sm" style={{ margin: 0 }}>Klienten</p>
              <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '2px 0 0' }}>{selectedUser.client_count}</p>
            </div>
            <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8 }}>
              <p className="text-muted text-sm" style={{ margin: 0 }}>Sitzungen</p>
              <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '2px 0 0' }}>{selectedUser.session_count}</p>
            </div>
            <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8 }}>
              <p className="text-muted text-sm" style={{ margin: 0 }}>Rechnungen</p>
              <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '2px 0 0' }}>{selectedUser.invoice_count}</p>
            </div>
          </div>

          {/* Status Badges */}
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, background: selectedUser.is_active ? '#e8f5e9' : '#fef2f2', color: selectedUser.is_active ? '#2e7d32' : '#991b1b' }}>
              {selectedUser.is_active ? 'Aktiv' : 'Gesperrt'}
            </span>
            <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, background: '#f0f4ff', color: '#4285F4' }}>
              {ROLE_LABELS[selectedUser.role] || selectedUser.role}
            </span>
            <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, background: badge.bg, color: badge.color }}>
              {PLAN_LABELS[planKey] || planKey || 'Kein Plan'}
            </span>
          </div>
          <p className="text-muted text-sm" style={{ marginTop: 8 }}>
            Registriert: {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('de-DE') : '-'}
            {selectedUser.last_login_at && <> | Letzter Login: {new Date(selectedUser.last_login_at).toLocaleString('de-DE')}</>}
          </p>

          {/* Abonnement Section */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>Abonnement</h4>
            {sub ? (
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <span className="text-muted text-sm">Plan: </span>
                  <select
                    value={sub.plan_type}
                    onChange={async (e) => {
                      const newPlan = e.target.value;
                      if (newPlan === sub.plan_type) return;
                      if (!window.confirm(`Plan auf "${PLAN_LABELS[newPlan] || newPlan}" ändern?`)) return;
                      try {
                        await adminApi.updateSubscription(sub.id, { plan_type: newPlan });
                        await loadSubs();
                        showToast(`Plan geändert: ${PLAN_LABELS[newPlan] || newPlan}`, 'success');
                      } catch (err: any) { showToast(err.response?.data?.detail || 'Fehler', 'error'); }
                    }}
                    style={{ padding: '4px 8px', borderRadius: 6, fontSize: '0.8rem', border: '1px solid var(--border)', background: 'var(--card-bg)', cursor: 'pointer' }}
                  >
                    <option value="free">Free Trial</option>
                    <option value="basic">Einzelcoach</option>
                    <option value="team_s">Professional S</option>
                    <option value="team_m">Professional M</option>
                    <option value="enterprise">Organisation</option>
                  </select>
                </div>
                <div>
                  <span className="text-muted text-sm">Status: </span>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, color: sub.status === 'beta' ? BETA_BADGE.color : statusColor(sub.status), background: sub.status === 'beta' ? BETA_BADGE.bg : sub.status === 'active' ? '#e8f5e9' : sub.status === 'trial' ? '#fff8e1' : '#fef2f2' }}>
                    {sub.status === 'active' ? 'Aktiv' : sub.status === 'trial' ? 'Trial' : sub.status === 'beta' ? 'Beta' : sub.status}
                  </span>
                </div>
                <div>
                  <span className="text-muted text-sm">Preis: </span>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {sub.price_monthly_eur != null ? `${Number(sub.price_monthly_eur).toFixed(2).replace('.', ',')} EUR/Mon.` : '0 EUR'}
                  </span>
                </div>
                {sub.trial_ends_at && (
                  <div>
                    <span className="text-muted text-sm">{sub.status === 'beta' ? 'Beta bis: ' : 'Trial bis: '}</span>
                    <span style={{ fontSize: '0.85rem' }}>{new Date(sub.trial_ends_at).toLocaleDateString('de-DE')}</span>
                  </div>
                )}
                {sub.status !== 'beta' && (
                  <button className="btn btn-sm" style={{ background: BETA_BADGE.bg, color: BETA_BADGE.color, border: 'none', fontWeight: 600, fontSize: '0.75rem' }} onClick={async () => {
                    if (!window.confirm(`Beta-Zugang (4 Wochen, alle Features) für ${selectedUser.first_name} ${selectedUser.last_name} gewähren?`)) return;
                    try { await adminApi.grantBeta(selectedUser.id, 4); await loadSubs(); loadDetail(selectedUser.id); showToast('Beta-Zugang gewährt (4 Wochen)', 'success'); }
                    catch (err: any) { showToast(err.response?.data?.detail || 'Fehler', 'error'); }
                  }}>Beta freischalten</button>
                )}
                {sub.status === 'beta' && (
                  <button className="btn btn-sm" style={{ background: '#fef2f2', color: '#991b1b', border: 'none', fontWeight: 600, fontSize: '0.75rem' }} onClick={async () => {
                    if (!window.confirm(`Beta-Zugang für ${selectedUser.first_name} ${selectedUser.last_name} entziehen?`)) return;
                    try { await adminApi.revokeBeta(selectedUser.id); await loadSubs(); loadDetail(selectedUser.id); showToast('Beta-Zugang entzogen', 'success'); }
                    catch (err: any) { showToast(err.response?.data?.detail || 'Fehler', 'error'); }
                  }}>Beta entziehen</button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="text-muted text-sm">Kein Abo vorhanden</span>
                <button className="btn btn-sm" style={{ background: BETA_BADGE.bg, color: BETA_BADGE.color, border: 'none', fontWeight: 600, fontSize: '0.75rem' }} onClick={async () => {
                  if (!window.confirm(`Beta-Zugang (4 Wochen, alle Features) für ${selectedUser.first_name} ${selectedUser.last_name} gewähren?`)) return;
                  try { await adminApi.grantBeta(selectedUser.id, 4); await loadSubs(); loadDetail(selectedUser.id); showToast('Beta-Zugang gewährt (4 Wochen)', 'success'); }
                  catch (err: any) { showToast(err.response?.data?.detail || 'Fehler', 'error'); }
                }}>Beta freischalten</button>
              </div>
            )}
          </div>

          {/* Rolle Section */}
          {selectedUser.role !== 'superadmin' && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>Rolle ändern</h4>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={selectedUser.role}
                  onChange={async (e) => {
                    const newRole = e.target.value;
                    if (!window.confirm(`Rolle auf "${ROLE_LABELS[newRole]}" ändern?`)) {
                      e.target.value = selectedUser.role;
                      return;
                    }
                    try {
                      await adminApi.updateUserRole(selectedUser.id, newRole);
                      loadDetail(selectedUser.id);
                      showToast(`Rolle geändert: ${ROLE_LABELS[newRole]}`, 'success');
                    } catch { showToast('Fehler beim Ändern der Rolle', 'error'); }
                  }}
                  style={{ padding: '6px 12px', borderRadius: 6, fontSize: '0.85rem', border: '1px solid var(--border)', background: 'var(--card-bg)', cursor: 'pointer' }}
                >
                  <option value="coach">Coach</option>
                  <option value="admin">Admin</option>
                  <option value="consultant">Berater</option>
                  <option value="change_manager">Change-Manager</option>
                  <option value="pair_counselor">Paarberatung</option>
                </select>
                <span className="text-muted text-sm">Bestimmt die Zugriffsrechte dieses Nutzers</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Aktionen</h4>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className={`btn btn-sm ${selectedUser.is_active ? 'btn-danger' : 'btn-primary'}`} onClick={async () => {
                try {
                  await adminApi.updateUserStatus(selectedUser.id, !selectedUser.is_active);
                  loadDetail(selectedUser.id);
                  showToast(selectedUser.is_active ? 'Nutzer gesperrt' : 'Nutzer aktiviert', 'success');
                } catch { showToast('Fehler beim Ändern des Status', 'error'); }
              }}>
                {selectedUser.is_active ? <><Ban size={14} /> Sperren</> : <><CheckCircle size={14} /> Aktivieren</>}
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => handleResetPassword(selectedUser.id, `${selectedUser.first_name} ${selectedUser.last_name}`, selectedUser.email)}>
                <KeyRound size={14} /> Passwort zurücksetzen
              </button>
              {selectedUser.role !== 'superadmin' && (
                <button className="btn btn-sm btn-danger" onClick={async () => {
                  if (!window.confirm(`${selectedUser.first_name} ${selectedUser.last_name} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
                  try {
                    await adminApi.deleteUser(selectedUser.id);
                    setSelectedUser(null); loadUsers(); loadSubs();
                    showToast('Nutzer gelöscht', 'info');
                  } catch { showToast('Fehler beim Löschen', 'error'); }
                }}><Trash2 size={14} /> Löschen</button>
              )}
            </div>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.78rem' }}>
              Passwort zurücksetzen generiert ein temporäres Passwort und sendet es per E-Mail an den Nutzer.
            </p>

            {/* Password Reset Result - shown inline when email failed */}
            {resetResult?.tempPw && (
              <div style={{ padding: 14, borderRadius: 8, background: '#fff8e1', border: '1px solid #f0d78c' }}>
                <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#92400e', fontSize: '0.85rem' }}>
                  E-Mail konnte nicht gesendet werden
                </p>
                <p style={{ margin: '0 0 8px', fontSize: '0.82rem', color: '#78350f' }}>
                  Bitte geben Sie das temporäre Passwort manuell an {resetResult.email} weiter:
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{ padding: '6px 14px', background: '#fff', borderRadius: 6, fontSize: '1rem', fontWeight: 700, letterSpacing: 1, border: '1px solid #e5e0d5' }}>
                    {resetResult.tempPw}
                  </code>
                  <button className="btn btn-sm btn-secondary" onClick={() => {
                    navigator.clipboard.writeText(resetResult.tempPw!);
                    showToast('Passwort kopiert', 'success');
                  }}>Kopieren</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- List View ---
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: 250 }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input value={search} onChange={(e) => { setSearch(e.target.value); loadUsers(e.target.value); }}
              placeholder="Suche..." style={{ paddingLeft: 32, width: '100%', fontSize: '0.85rem' }} />
          </div>
          <span className="text-muted text-sm">{total} Nutzer gesamt</span>
        </div>
        <button className="btn btn-sm btn-primary" onClick={() => setShowCreate(true)}>
          <UserPlus size={14} /> Nutzer anlegen
        </button>
      </div>

      {showCreate && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <h4 style={{ marginBottom: 12 }}>Neuen Nutzer anlegen</h4>
          <div className="form-row">
            <div className="form-group"><label>Vorname</label><input value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} /></div>
            <div className="form-group"><label>Nachname</label><input value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>E-Mail</label><input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} /></div>
            <div className="form-group"><label>Passwort</label><input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Rolle</label>
              <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} style={{ fontSize: '0.85rem' }}>
                <option value="coach">Coach</option>
                <option value="admin">Admin</option>
                <option value="consultant">Berater</option>
                <option value="change_manager">Change-Manager</option>
                <option value="pair_counselor">Paarberatung</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm btn-primary" onClick={handleCreate}>Anlegen</button>
            <button className="btn btn-sm btn-secondary" onClick={() => setShowCreate(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 700 }}>
          <thead>
            <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '10px 16px', textAlign: 'left' }}>E-Mail</th>
              <th style={{ padding: '10px 16px', textAlign: 'left' }}>Rolle</th>
              <th style={{ padding: '10px 16px', textAlign: 'left' }}>Plan</th>
              <th style={{ padding: '10px 16px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '10px 16px', textAlign: 'left' }}>Registriert</th>
              <th style={{ padding: '10px 16px', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const p = u.plan || '';
              const isBeta = u.plan_status === 'beta';
              const c = isBeta ? BETA_BADGE : (PLAN_BADGE[p] || { bg: 'var(--bg)', color: 'var(--text-muted)' });
              return (
                <tr key={u.id} style={{ borderBottom: '1px solid #f0ede8' }}>
                  <td style={{ padding: '10px 16px' }}><strong>{u.first_name} {u.last_name}</strong></td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-muted)' }}>{u.email}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.75rem', background: '#f0f4ff', color: '#4285F4' }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, background: c.bg, color: c.color }}>
                      {isBeta ? 'Beta-Tester' : (PLAN_LABELS[p] || p || '-')}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, background: u.is_active ? '#e8f5e9' : '#fef2f2', color: u.is_active ? '#2e7d32' : '#991b1b' }}>
                      {u.is_active ? 'Aktiv' : 'Gesperrt'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('de-DE') : '-'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => loadDetail(u.id)}>
                      <Eye size={14} /> Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Admin System ---
function AdminSystem({ onBack }: { onBack: () => void }) {
  const [status, setStatus] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminApi.systemStatus().then(r => setStatus(r.data)).catch(() => {}),
      adminApi.systemLogs({ limit: 30 }).then(r => setLogs(r.data.logs)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[...Array(5)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
      <SkeletonTable rows={5} />
    </div>
  );

  return (
    <div>
      <button className="btn btn-sm btn-secondary" onClick={onBack} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ArrowLeft size={16} /> Zurück zur Übersicht
      </button>
      {status && (() => {
        const barColor = (pct: number, thresholds: [number, number]) =>
          pct > thresholds[1] ? '#dc2626' : pct > thresholds[0] ? '#B8860B' : pct > 50 ? '#4285F4' : '#2e7d32';
        const metrics = [
          { label: 'CPU', value: `${status.cpu_percent}%`, pct: status.cpu_percent, color: barColor(status.cpu_percent, [60, 80]) },
          { label: 'RAM', value: `${status.memory_percent}%`, pct: status.memory_percent, color: barColor(status.memory_percent, [70, 85]) },
          { label: 'Disk', value: `${status.disk_percent}%`, pct: status.disk_percent, color: barColor(status.disk_percent, [70, 90]) },
          { label: 'Datenbank', value: status.db_size, pct: -1, color: '#8B6F47' },
          { label: 'Upload-Dateien', value: `${status.upload_files}`, pct: -1, color: '#3D2B1F' },
        ];
        return (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
              {metrics.map(({ label, value, pct, color }) => (
                <div key={label} className="card" style={{ padding: 16, textAlign: 'center' }}>
                  <p style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 4px', color }}>{value}</p>
                  <p className="text-muted text-sm" style={{ margin: '0 0 8px' }}>{label}</p>
                  {pct >= 0 && (
                    <div style={{ height: 6, background: '#f0ede8', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.3s ease' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="card" style={{ padding: 16, marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 10px' }}>Warnschwellen</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
                {[
                  { label: 'CPU > 80%', color: '#dc2626', active: status.cpu_percent > 80 },
                  { label: 'RAM > 85%', color: '#dc2626', active: status.memory_percent > 85 },
                  { label: 'Disk > 90%', color: '#dc2626', active: status.disk_percent > 90 },
                ].map(({ label, color, active }) => (
                  <div key={label} style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600,
                    background: active ? '#fef2f2' : '#f0ede8',
                    color: active ? color : 'var(--text-muted)',
                  }}>
                    {active ? '!' : ''} {label} → Rot
                  </div>
                ))}
              </div>
            </div>
          </>
        );
      })()}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3>System-Logs</h3>
        <button className="btn btn-sm btn-secondary" onClick={async () => {
          const res = await adminApi.systemLogs({ limit: 30 }); setLogs(res.data.logs);
        }}><RefreshCw size={14} /> Aktualisieren</button>
      </div>
      {logs.length === 0 ? (
        <p className="text-muted">Keine Logs vorhanden.</p>
      ) : (
        <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: 600 }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Level</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nachricht</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Endpoint</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Datum</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid #f0ede8' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600,
                      background: l.level === 'error' ? '#fef2f2' : l.level === 'warning' ? '#fff8e1' : '#f0f4ff',
                      color: l.level === 'error' ? '#991b1b' : l.level === 'warning' ? '#B8860B' : '#4285F4' }}>
                      {l.level}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.message}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{l.endpoint || '-'}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{new Date(l.created_at).toLocaleString('de-DE')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Admin Support ---
function AdminSupport() {
  const { showToast } = useToast();
  const [tickets, setTickets] = useState<any[]>([]);
  const [slaKpi, setSlaKpi] = useState<any>(null);
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState<any>(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const loadTickets = async () => {
    const res = await adminApi.supportTickets({ status: filter || undefined });
    setTickets(res.data.tickets);
    if (res.data.sla_kpi) setSlaKpi(res.data.sla_kpi);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTickets(); }, [filter]);

  const statusLabels: Record<string, string> = { open: 'Offen', in_progress: 'In Bearbeitung', resolved: 'Gelöst' };
  const priorityLabels: Record<string, string> = { low: 'Niedrig', normal: 'Normal', high: 'Hoch', urgent: 'Dringend' };
  const priorityColors: Record<string, string> = { low: '#94a3b8', normal: '#3b82f6', high: '#f59e0b', urgent: '#ef4444' };
  const categoryLabels: Record<string, string> = { technisch: 'Technisch', abrechnung: 'Abrechnung', termine: 'Termine', konto: 'Konto', sonstiges: 'Sonstiges', feature: 'Feature-Wunsch' };

  const fmtDateTime = (d: string) => new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const timeAgo = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `vor ${mins} Min.`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    return `vor ${Math.floor(hours / 24)} Tag${Math.floor(hours / 24) > 1 ? 'en' : ''}`;
  };

  const openDetail = async (id: string) => {
    try {
      const res = await adminApi.ticketDetail(id);
      setDetail(res.data);
      setReplyText('');
    } catch { showToast('Ticket konnte nicht geladen werden', 'error'); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !detail) return;
    setReplying(true);
    try {
      await adminApi.replyTicket(detail.id, { message: replyText });
      showToast('Antwort gesendet', 'success');
      setReplyText('');
      openDetail(detail.id);
      loadTickets();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    } finally { setReplying(false); }
  };

  // --- Detail View ---
  if (detail) {
    return (
      <div>
        <button className="btn btn-secondary" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => setDetail(null)}>
          <ArrowLeft size={16} /> Zurück zur Liste
        </button>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ background: 'var(--sidebar-bg)', color: 'var(--sidebar-text)', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Ticket: {detail.subject}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: detail.status === 'resolved' ? '#dcfce7' : detail.status === 'in_progress' ? '#dbeafe' : '#fef3c7', color: detail.status === 'resolved' ? '#166534' : detail.status === 'in_progress' ? '#1e40af' : '#92400e' }}>
                {statusLabels[detail.status] || detail.status}
              </span>
              {detail.priority && detail.priority !== 'normal' && (
                <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, border: `1px solid ${priorityColors[detail.priority]}`, color: priorityColors[detail.priority] }}>
                  {priorityLabels[detail.priority]}
                </span>
              )}
              {detail.category && <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>{categoryLabels[detail.category] || detail.category}</span>}
            </div>
          </div>

          <div style={{ padding: 24 }}>
            {/* Meta */}
            <div className="text-muted text-sm" style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span>Von: <strong>{detail.user_name}</strong> ({detail.user_email})</span>
              <span>Quelle: {detail.source === 'client' ? 'Klient' : 'Coach'}</span>
              {detail.sla_due_at && !detail.first_response_at && detail.status !== 'resolved' && (
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600,
                  background: detail.sla_status === 'breached' ? '#fef2f2' : detail.sla_status === 'warning' ? '#fffbeb' : '#f0fdf4',
                  color: detail.sla_status === 'breached' ? '#ef4444' : detail.sla_status === 'warning' ? '#f59e0b' : '#22c55e',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  <Clock size={10} />
                  {detail.sla_status === 'breached' ? 'SLA überschritten' : `SLA bis ${fmtDateTime(detail.sla_due_at)}`}
                </span>
              )}
              {detail.first_response_at && (
                <span style={{ fontSize: '0.75rem', color: '#22c55e' }}>
                  Erste Antwort: {fmtDateTime(detail.first_response_at)}
                </span>
              )}
            </div>

            {/* Original message */}
            <div style={{ padding: 16, border: '1px solid var(--border-light)', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong>{detail.user_name}</strong>
                <span className="text-muted text-sm">{detail.created_at ? fmtDateTime(detail.created_at) : ''}</span>
              </div>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{detail.message}</p>
            </div>

            {/* Replies */}
            {detail.replies?.map((r: any) => (
              <div key={r.id} style={{
                padding: 16, borderRadius: 8, marginBottom: 12,
                background: r.sender_type === 'admin' ? '#eff6ff' : r.sender_type === 'client' ? '#faf0ee' : 'var(--bg)',
                borderLeft: r.sender_type === 'admin' ? '4px solid #3b82f6' : r.sender_type === 'client' ? '4px solid var(--primary)' : '4px solid var(--border)',
                marginLeft: r.sender_type === 'admin' ? 24 : 0,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <strong style={{ color: r.sender_type === 'admin' ? '#2563eb' : 'inherit' }}>
                    {r.sender_name} {r.is_internal ? '(intern)' : ''}
                  </strong>
                  <span className="text-muted text-sm">{r.created_at ? fmtDateTime(r.created_at) : ''}</span>
                </div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{r.message}</p>
              </div>
            ))}

            {/* Internal notes */}
            {detail.internal_notes && (
              <div style={{ padding: 12, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 16, fontSize: '0.85rem' }}>
                <strong>Interne Notiz:</strong> {detail.internal_notes}
              </div>
            )}

            {/* Status actions */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, paddingTop: 12, borderTop: '1px solid var(--border-light)' }}>
              {detail.status !== 'in_progress' && detail.status !== 'resolved' && (
                <button className="btn btn-secondary" style={{ fontSize: '0.85rem' }} onClick={async () => {
                  await adminApi.updateTicket(detail.id, { status: 'in_progress' });
                  showToast('Status: In Bearbeitung', 'success');
                  openDetail(detail.id); loadTickets();
                }}>In Bearbeitung setzen</button>
              )}
              {detail.status !== 'resolved' && (
                <button className="btn btn-secondary" style={{ fontSize: '0.85rem', color: '#166534', borderColor: '#bbf7d0' }} onClick={async () => {
                  await adminApi.updateTicket(detail.id, { status: 'resolved' });
                  showToast('Ticket gelöst', 'success');
                  openDetail(detail.id); loadTickets();
                }}>Als gelöst markieren</button>
              )}
            </div>

            {/* Reply box */}
            {detail.status !== 'resolved' && (
              <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 16 }}>
                <textarea
                  placeholder="Ihre Antwort..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  style={{ width: '100%', minHeight: 80, resize: 'vertical', borderRadius: 8, border: '1px solid var(--border)', padding: 12, fontSize: '0.9rem', marginBottom: 8 }}
                />
                <div style={{ textAlign: 'right' }}>
                  <button className="btn btn-primary" onClick={handleReply} disabled={replying || !replyText.trim()}>
                    <Send size={14} /> {replying ? 'Wird gesendet...' : 'Antworten'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- List View ---
  const counts = {
    all: tickets.length,
    open: tickets.filter((t) => t.status === 'open').length,
    inProgress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
  };

  return (
    <div>
      {/* SLA KPIs */}
      {slaKpi && slaKpi.total > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div className="card" style={{ padding: '12px 20px', flex: 1, minWidth: 120, textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary-dark)' }}>{slaKpi.total}</div>
            <div className="text-muted text-sm">Tickets gesamt</div>
          </div>
          <div className="card" style={{ padding: '12px 20px', flex: 1, minWidth: 120, textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>{slaKpi.avg_response_hours != null ? `${slaKpi.avg_response_hours}h` : '--'}</div>
            <div className="text-muted text-sm">Avg. Reaktionszeit</div>
          </div>
          <div className="card" style={{ padding: '12px 20px', flex: 1, minWidth: 120, textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#22c55e' }}>{slaKpi.sla_met_pct != null ? `${slaKpi.sla_met_pct}%` : '--'}</div>
            <div className="text-muted text-sm">SLA erfüllt</div>
          </div>
          <div className="card" style={{ padding: '12px 20px', flex: 1, minWidth: 120, textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: slaKpi.breached > 0 ? '#ef4444' : '#22c55e' }}>{slaKpi.breached}</div>
            <div className="text-muted text-sm">SLA verletzt</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['', 'open', 'in_progress', 'resolved'].map((f) => (
          <button key={f} className={`chip ${filter === f ? 'chip-active' : ''}`} onClick={() => setFilter(f)}>
            {f === '' ? `Alle (${counts.all})` : f === 'open' ? `Offen (${counts.open})` : f === 'in_progress' ? `In Bearbeitung (${counts.inProgress})` : `Gelöst (${counts.resolved})`}
          </button>
        ))}
      </div>
      {tickets.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p className="text-muted">Keine Support-Anfragen vorhanden.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tickets.map((t) => (
            <div key={t.id} className="card" style={{ padding: '16px 20px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onClick={() => openDetail(t.id)}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 4, height: 32, borderRadius: 2, background: priorityColors[t.priority] || '#94a3b8' }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{t.subject}</div>
                      <div className="text-muted text-sm" style={{ marginTop: 2 }}>
                        {t.message?.length > 100 ? t.message.substring(0, 100) + '...' : t.message}
                      </div>
                    </div>
                  </div>
                  <div className="text-muted text-sm" style={{ marginTop: 6, display: 'flex', gap: 12, alignItems: 'center', marginLeft: 12 }}>
                    <span>{t.source === 'client' ? 'Klient' : 'Coach'}: {t.user_name}</span>
                    {t.category && <span>{categoryLabels[t.category] || t.category}</span>}
                    <span>{t.created_at ? timeAgo(t.created_at) : ''}</span>
                    <span>{t.reply_count || 0} Antwort{(t.reply_count || 0) !== 1 ? 'en' : ''}</span>
                    {t.sla_status === 'breached' && (
                      <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 600, background: '#fef2f2', color: '#ef4444' }}>
                        <Clock size={10} /> SLA verletzt
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: t.status === 'resolved' ? '#dcfce7' : t.status === 'in_progress' ? '#dbeafe' : '#fef3c7', color: t.status === 'resolved' ? '#166534' : t.status === 'in_progress' ? '#1e40af' : '#92400e' }}>
                    {statusLabels[t.status] || t.status}
                  </span>
                  {t.priority !== 'normal' && (
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, border: `1px solid ${priorityColors[t.priority]}`, color: priorityColors[t.priority] }}>
                      {priorityLabels[t.priority]}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Admin Settings ---
function AdminEinstellungen() {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.getSettings()
      .then(r => setSettings(r.data))
      .catch(() => showToast('Einstellungen konnten nicht geladen werden', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings({
        maintenance_mode: settings.maintenance_mode,
        maintenance_message: settings.maintenance_message,
        max_coaches: settings.max_coaches,
        storage_limit_gb: settings.storage_limit_gb,
      });
      showToast('Einstellungen gespeichert', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMaintenance = async () => {
    const newMode = !settings.maintenance_mode;
    try {
      await adminApi.toggleMaintenance({
        enabled: newMode,
        message: settings.maintenance_message,
      });
      setSettings({ ...settings, maintenance_mode: newMode });
      showToast(newMode ? 'Wartungsmodus aktiviert' : 'Wartungsmodus deaktiviert', newMode ? 'info' : 'success');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    }
  };

  if (loading) return (
    <div style={{ display: 'grid', gap: 16 }}>
      {[...Array(3)].map((_, i) => <SkeletonCard key={i} />)}
    </div>
  );

  if (!settings) return <p className="text-muted">Einstellungen konnten nicht geladen werden.</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Maintenance Mode */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Shield size={18} style={{ color: '#8B6F47' }} />
          <h3 style={{ margin: 0 }}>Wartungsmodus</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button
            className={`btn btn-sm ${settings.maintenance_mode ? 'btn-danger' : 'btn-primary'}`}
            onClick={handleToggleMaintenance}
          >
            {settings.maintenance_mode ? 'Wartungsmodus deaktivieren' : 'Wartungsmodus aktivieren'}
          </button>
          <span style={{
            padding: '4px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600,
            background: settings.maintenance_mode ? '#fef2f2' : '#e8f5e9',
            color: settings.maintenance_mode ? '#991b1b' : '#2e7d32',
          }}>
            {settings.maintenance_mode ? 'AKTIV — Nutzer sehen 503' : 'Inaktiv'}
          </span>
        </div>
        <div className="form-group">
          <label>Wartungsnachricht</label>
          <textarea
            value={settings.maintenance_message || ''}
            onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
            rows={3}
            style={{ width: '100%', fontSize: '0.85rem' }}
          />
        </div>
      </div>

      {/* Platform Limits */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Settings size={18} style={{ color: '#8B6F47' }} />
          <h3 style={{ margin: 0 }}>Plattform-Limits</h3>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Max. Coaches</label>
            <input
              type="number"
              value={settings.max_coaches}
              onChange={(e) => setSettings({ ...settings, max_coaches: parseInt(e.target.value) || 0 })}
              min={1}
              style={{ width: 150 }}
            />
            <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>Maximale Anzahl registrierter Coaches</p>
          </div>
          <div className="form-group">
            <label>Storage-Limit (GB)</label>
            <input
              type="number"
              value={settings.storage_limit_gb}
              onChange={(e) => setSettings({ ...settings, storage_limit_gb: parseFloat(e.target.value) || 0 })}
              min={1}
              step={0.5}
              style={{ width: 150 }}
            />
            <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>Maximaler Speicherplatz für Uploads</p>
          </div>
        </div>
      </div>

      {/* Save */}
      <div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} style={{ marginRight: 4 }} />
          {saving ? 'Speichern...' : 'Einstellungen speichern'}
        </button>
        {settings.updated_at && (
          <span className="text-muted text-sm" style={{ marginLeft: 12 }}>
            Zuletzt aktualisiert: {new Date(settings.updated_at).toLocaleString('de-DE')}
          </span>
        )}
      </div>
    </div>
  );
}

// --- Admin Audit Log ---
function AdminAuditLog({ onBack }: { onBack: () => void }) {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    from_date: '',
    to_date: '',
  });
  const [page, setPage] = useState(0);
  const pageSize = 30;

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await adminApi.auditLogs({
        skip: page * pageSize,
        limit: pageSize,
        action: filters.action || undefined,
        entity_type: filters.entity_type || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(); }, [page, filters]);

  const actionStyle = (a: string): { bg: string; color: string; label: string } => {
    const map: Record<string, { bg: string; color: string; label: string }> = {
      create: { bg: '#e8f5e9', color: '#2e7d32', label: 'Erstellt' },
      read: { bg: '#f0f4ff', color: '#4285F4', label: 'Gelesen' },
      update: { bg: '#fff8e1', color: '#B8860B', label: 'Aktualisiert' },
      delete: { bg: '#fef2f2', color: '#c62828', label: 'Gelöscht' },
      export: { bg: '#e3f2fd', color: '#1565c0', label: 'Exportiert' },
      download: { bg: '#f3e5f5', color: '#7b1fa2', label: 'Heruntergeladen' },
      login: { bg: '#e0f2f1', color: '#00695c', label: 'Login' },
    };
    return map[a] || { bg: '#f5f5f5', color: '#888', label: a };
  };

  const statusBg = (s: string) => s === 'success' ? '#e8f5e9' : s === 'failure' ? '#fef2f2' : '#fff8e1';
  const statusColor = (s: string) => s === 'success' ? '#2e7d32' : s === 'failure' ? '#991b1b' : '#B8860B';

  const { showToast } = useToast();
  const handleExportCsv = async () => {
    try {
      const res = await adminApi.auditLogsExport({
        action: filters.action || undefined,
        entity_type: filters.entity_type || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AuditLog_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('CSV exportiert', 'success');
    } catch { showToast('Export fehlgeschlagen', 'error'); }
  };

  return (
    <div>
      <button className="btn btn-sm btn-secondary" onClick={onBack} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ArrowLeft size={16} /> Zurück zur Übersicht
      </button>
      {/* Filters */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Aktion</label>
            <select value={filters.action} onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(0); }}
              style={{ fontSize: '0.85rem', minWidth: 140 }}>
              <option value="">Alle</option>
              <option value="create">Erstellt</option>
              <option value="update">Aktualisiert</option>
              <option value="delete">Gelöscht</option>
              <option value="read">Gelesen</option>
              <option value="export">Exportiert</option>
              <option value="download">Heruntergeladen</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Bereich</label>
            <select value={filters.entity_type} onChange={(e) => { setFilters({ ...filters, entity_type: e.target.value }); setPage(0); }}
              style={{ fontSize: '0.85rem', minWidth: 140 }}>
              <option value="">Alle</option>
              <option value="user">Nutzer</option>
              <option value="client">Klient</option>
              <option value="case">Fall</option>
              <option value="session">Sitzung</option>
              <option value="invoice">Rechnung</option>
              <option value="offer">Angebot</option>
              <option value="document">Dokument</option>
              <option value="privacy">Datenschutz</option>
              <option value="change_request">Änderungsantrag</option>
              <option value="audit_log">Audit-Log</option>
              <option value="settings">Einstellungen</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Von</label>
            <input type="date" value={filters.from_date} onChange={(e) => { setFilters({ ...filters, from_date: e.target.value }); setPage(0); }}
              style={{ fontSize: '0.85rem' }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Bis</label>
            <input type="date" value={filters.to_date} onChange={(e) => { setFilters({ ...filters, to_date: e.target.value }); setPage(0); }}
              style={{ fontSize: '0.85rem' }} />
          </div>
          <button className="btn btn-sm btn-secondary" onClick={() => { setFilters({ action: '', entity_type: '', from_date: '', to_date: '' }); setPage(0); }}>
            Zurücksetzen
          </button>
          <button className="btn btn-sm btn-secondary" onClick={handleExportCsv} style={{ marginLeft: 'auto' }}>
            <Download size={12} style={{ marginRight: 4 }} /> CSV Export
          </button>
          <span className="text-muted text-sm">{total} Einträge</span>
        </div>
      </div>

      {loading ? <SkeletonTable rows={8} /> : logs.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p className="text-muted">Keine Audit-Log-Einträge gefunden.</p>
        </div>
      ) : (
        <>
          <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: 700 }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Zeitpunkt</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Benutzer</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Aktion</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Bereich</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Beschreibung</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} style={{ borderBottom: '1px solid #f0ede8' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(l.timestamp).toLocaleString('de-DE')}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{l.user_name}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {(() => { const s = actionStyle(l.action); return (
                        <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600, background: s.bg, color: s.color }}>
                          {s.label}
                        </span>
                      ); })()}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{l.entity_type}</td>
                    <td style={{ padding: '8px 12px', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.description || '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600,
                        background: statusBg(l.status), color: statusColor(l.status) }}>
                        {l.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button className="btn btn-sm btn-secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                Zurück
              </button>
              <span className="text-muted text-sm" style={{ padding: '6px 0' }}>
                Seite {page + 1} von {Math.ceil(total / pageSize)}
              </span>
              <button className="btn btn-sm btn-secondary" disabled={(page + 1) * pageSize >= total} onClick={() => setPage(p => p + 1)}>
                Weiter
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- Business Intelligence ---
function AdminBI({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    setLoading(true);
    adminApi.bi({ period }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div><div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>{[...Array(5)].map((_, i) => <SkeletonCard key={i} />)}</div><SkeletonTable rows={5} /></div>;

  if (!data) return <div className="card" style={{ padding: 40, textAlign: 'center' }}><p className="text-muted">Daten konnten nicht geladen werden</p></div>;

  const kpis = data.kpis || {};
  const funnel = data.funnel || {};
  const coaches = data.coach_activity || [];

  return (
    <div>
      <button className="btn btn-sm btn-secondary" onClick={onBack} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ArrowLeft size={16} /> Zurück zur Übersicht
      </button>
      {/* Period Toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg)', borderRadius: 8, padding: 3 }}>
          {[{ v: '7', l: '7 Tage' }, { v: '30', l: '30 Tage' }, { v: '90', l: '90 Tage' }].map(p => (
            <button key={p.v} className={`chip ${period === p.v ? 'chip-active' : ''}`} onClick={() => setPeriod(p.v)} style={{ fontSize: '0.75rem', padding: '4px 12px' }}>
              {p.l}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'MRR', value: `€ ${(kpis.mrr || 0).toFixed(0)}`, trend: kpis.mrr_change, color: '#2563eb' },
          { label: 'Aktive Coaches', value: kpis.active_paid || 0, trend: null, color: '#10b981' },
          { label: 'Trial', value: kpis.active_trial || 0, trend: null, color: '#f59e0b' },
          { label: 'Churn Rate', value: `${(kpis.churn_rate || 0).toFixed(1)}%`, trend: null, color: kpis.churn_rate > 10 ? '#ef4444' : '#10b981' },
          { label: 'ARPU', value: `€ ${(kpis.arpu || 0).toFixed(0)}`, trend: null, color: '#8b5cf6' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: 16, textAlign: 'center' }}>
            <p style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0 0 4px', color }}>{value}</p>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.75rem' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Conversion Funnel */}
      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 16 }}>Conversion Funnel</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { label: 'Registriert', value: funnel.registered || 0, pct: 100, color: '#2563eb' },
            { label: 'Trial aktiv', value: funnel.trial || 0, pct: funnel.registered ? Math.round((funnel.trial / funnel.registered) * 100) : 0, color: '#06b6d4' },
            { label: 'Zahlend', value: funnel.paid || 0, pct: funnel.registered ? Math.round((funnel.paid / funnel.registered) * 100) : 0, color: '#10b981' },
          ].map(f => (
            <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ minWidth: 80, fontSize: '0.82rem', fontWeight: 500 }}>{f.label}</span>
              <div style={{ flex: 1, height: 28, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ width: `${f.pct}%`, height: '100%', background: f.color, borderRadius: 6, display: 'flex', alignItems: 'center', paddingLeft: 10, color: 'white', fontSize: '0.78rem', fontWeight: 600, minWidth: 30 }}>
                  {f.value}
                </div>
              </div>
              <span style={{ minWidth: 40, fontSize: '0.78rem', color: '#6b7280' }}>{f.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Coach Activity */}
      <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-light)' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>Coach-Aktivität (letzte {period} Tage)</h3>
        </div>
        {coaches.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}><p className="text-muted">Keine aktiven Coaches</p></div>
        ) : (
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: 700 }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Coach</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Plan</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Letzter Login</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Klienten</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Sitzungen</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Rechnungen</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Risiko</th>
              </tr>
            </thead>
            <tbody>
              {coaches.map((c: any) => {
                const activity = (c.clients || 0) + (c.sessions || 0) + (c.invoices || 0);
                const risk = activity === 0 ? 'Hoch' : activity < 5 ? 'Mittel' : 'Gering';
                const riskColor = risk === 'Hoch' ? '#ef4444' : risk === 'Mittel' ? '#f59e0b' : '#10b981';
                return (
                  <tr key={c.user_id} style={{ borderBottom: '1px solid #f0ede8' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', background: '#dbeafe', color: '#2563eb', fontSize: '0.68rem', fontWeight: 700, marginRight: 8 }}>
                        {(c.name || c.email || '?')[0].toUpperCase()}
                      </span>
                      {c.name || c.email}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{c.plan || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{c.last_login ? new Date(c.last_login).toLocaleDateString('de-DE') : '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{c.clients || 0}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{c.sessions || 0}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{c.invoices || 0}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: riskColor, marginRight: 4 }}></span>
                      <span style={{ fontSize: '0.72rem', color: riskColor, fontWeight: 600 }}>{risk}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// --- Announcements ---
function AdminAnnouncements() {
  const { showToast } = useToast();
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', announcement_type: 'feature', target: 'all', channels: { in_app: true, email: true, changelog: false } });
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.announcements().then(r => setAnnouncements(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSend = () => {
    if (!form.title || !form.message) { showToast('Titel und Nachricht sind Pflichtfelder', 'error'); return; }
    setSending(true);
    adminApi.createAnnouncement(form)
      .then(() => { showToast('Ankündigung gesendet', 'success'); setShowForm(false); setForm({ title: '', message: '', announcement_type: 'feature', target: 'all', channels: { in_app: true, email: true, changelog: false } }); load(); })
      .catch(() => showToast('Fehler beim Senden', 'error'))
      .finally(() => setSending(false));
  };

  const typeLabels: Record<string, string> = { feature: 'Feature-Update', maintenance: 'Wartung', incident: 'Störung', info: 'Info', promo: 'Aktion' };
  const typeEmojis: Record<string, string> = { feature: '🆕', maintenance: '🔧', incident: '⚠️', info: '📋', promo: '🎉' };

  if (loading) return <div><SkeletonTable rows={5} /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Ankündigungen & Broadcasts</h3>
        <button className="btn btn-primary" style={{ fontSize: '0.8rem' }} onClick={() => setShowForm(!showForm)}>
          {showForm ? <><X size={14} style={{ marginRight: 4 }} /> Abbrechen</> : <><Plus size={14} style={{ marginRight: 4 }} /> Neue Ankündigung</>}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Typ</label>
              <select value={form.announcement_type} onChange={e => setForm({ ...form, announcement_type: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-light)', borderRadius: 8, fontSize: '0.85rem' }}>
                <option value="feature">🆕 Feature-Update</option>
                <option value="maintenance">🔧 Wartung geplant</option>
                <option value="incident">⚠️ Störungsmeldung</option>
                <option value="info">📋 Allgemeine Info</option>
                <option value="promo">🎉 Sonderaktion</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Zielgruppe</label>
              <select value={form.target} onChange={e => setForm({ ...form, target: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-light)', borderRadius: 8, fontSize: '0.85rem' }}>
                <option value="all">Alle aktiven Coaches</option>
                <option value="paid">Nur zahlende Coaches</option>
                <option value="trial">Nur Trial-Coaches</option>
                <option value="professional">Nur Professional-Plan</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Titel</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="z.B. Neu: Geführte UI-Touren" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-light)', borderRadius: 8, fontSize: '0.85rem' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Nachricht</label>
            <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Beschreibung der Ankündigung..." style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-light)', borderRadius: 8, fontSize: '0.85rem', resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            {[
              { key: 'in_app', label: 'In-App Banner' },
              { key: 'email', label: 'E-Mail' },
              { key: 'changelog', label: 'Changelog' },
            ].map(ch => (
              <label key={ch.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={(form.channels as any)[ch.key]} onChange={e => setForm({ ...form, channels: { ...form.channels, [ch.key]: e.target.checked } })} />
                {ch.label}
              </label>
            ))}
          </div>
          <button className="btn btn-primary" disabled={sending} onClick={handleSend} style={{ fontSize: '0.82rem' }}>
            <Send size={14} style={{ marginRight: 4 }} /> {sending ? 'Wird gesendet...' : 'Ankündigung senden'}
          </button>
        </div>
      )}

      {announcements.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}><p className="text-muted">Noch keine Ankündigungen gesendet</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {announcements.map((a: any, i: number) => (
            <div key={a.id} style={{ padding: '14px 20px', borderBottom: i < announcements.length - 1 ? '1px solid #f0ede8' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                  {typeEmojis[a.announcement_type] || '📋'} {a.title}
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {new Date(a.created_at).toLocaleDateString('de-DE')} — {typeLabels[a.announcement_type] || a.announcement_type}
                  {a.channels?.in_app && ' · In-App'}
                  {a.channels?.email && ' · E-Mail'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{a.sent_count} gesendet</div>
                <div className="text-muted" style={{ fontSize: '0.72rem' }}>{a.read_count} gelesen ({a.sent_count > 0 ? Math.round((a.read_count / a.sent_count) * 100) : 0}%)</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Main Admin Page ---
export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'nutzer' | 'system' | 'support' | 'einstellungen' | 'audit' | 'wartung' | 'bi' | 'announce'>('dashboard');

  return (
    <div>
      <div className="mb-24">
        <h1>Plattform-Administration</h1>
        <p className="text-muted text-sm">Nutzer, Abonnements, System und Support verwalten</p>
      </div>

      <div className="category-chips mb-24">
        <button className={`chip ${activeTab === 'dashboard' ? 'chip-active' : ''}`} onClick={() => setActiveTab('dashboard')}>
          <LayoutDashboard size={14} style={{ marginRight: 4 }} /> Übersicht
        </button>
        <button className={`chip ${activeTab === 'nutzer' ? 'chip-active' : ''}`} onClick={() => setActiveTab('nutzer')}>
          <Users size={14} style={{ marginRight: 4 }} /> Nutzer
        </button>
<button className={`chip ${activeTab === 'system' ? 'chip-active' : ''}`} onClick={() => setActiveTab('system')}>
          <Server size={14} style={{ marginRight: 4 }} /> System
        </button>
        <button className={`chip ${activeTab === 'support' ? 'chip-active' : ''}`} onClick={() => setActiveTab('support')}>
          <HeadphonesIcon size={14} style={{ marginRight: 4 }} /> Support
        </button>
        <button className={`chip ${activeTab === 'einstellungen' ? 'chip-active' : ''}`} onClick={() => setActiveTab('einstellungen')}>
          <Settings size={14} style={{ marginRight: 4 }} /> Einstellungen
        </button>
        <button className={`chip ${activeTab === 'wartung' ? 'chip-active' : ''}`} onClick={() => setActiveTab('wartung')}>
          <Wrench size={14} style={{ marginRight: 4 }} /> Wartung
        </button>
        <button className={`chip ${activeTab === 'audit' ? 'chip-active' : ''}`} onClick={() => setActiveTab('audit')}>
          <FileText size={14} style={{ marginRight: 4 }} /> Audit-Log
        </button>
        <button className={`chip ${activeTab === 'bi' ? 'chip-active' : ''}`} onClick={() => setActiveTab('bi')}>
          <BarChart3 size={14} style={{ marginRight: 4 }} /> Business Intelligence
        </button>
        <button className={`chip ${activeTab === 'announce' ? 'chip-active' : ''}`} onClick={() => setActiveTab('announce')}>
          <Megaphone size={14} style={{ marginRight: 4 }} /> Ankündigungen
        </button>
      </div>

      {activeTab === 'dashboard' && <AdminDashboard />}
      {activeTab === 'nutzer' && <AdminNutzer />}
{activeTab === 'system' && <AdminSystem onBack={() => setActiveTab('dashboard')} />}
      {activeTab === 'support' && <AdminSupport />}
      {activeTab === 'einstellungen' && <AdminEinstellungen />}
      {activeTab === 'wartung' && <AdminWartung />}
      {activeTab === 'audit' && <AdminAuditLog onBack={() => setActiveTab('dashboard')} />}
      {activeTab === 'bi' && <AdminBI onBack={() => setActiveTab('dashboard')} />}
      {activeTab === 'announce' && <AdminAnnouncements />}
    </div>
  );
}
